import type { OperationProgress } from "../../types";
import type { AmlogicBatchEvent } from "./AmlogicBatchRunner";
import { isRemoteFirmwareFile } from "../../files/RemoteFirmwareFile";

export type AmlogicBatchGeneration = "adnl-v3" | "legacy-v2";
export type AmlogicBatchTargetState = "queued" | "running" | "completed" | "failed" | "cancelled" | "skipped";

export interface AmlogicBatchTargetSeed {
  sessionId: string;
  label: string;
  eligible: boolean;
  reason?: string;
}

export interface AmlogicBatchTargetRun {
  sessionId: string;
  label: string;
  state: AmlogicBatchTargetState;
  reason?: string;
  jobId?: string;
  progress?: OperationProgress;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

export interface AmlogicBatchRunSnapshot {
  id: string;
  generation: AmlogicBatchGeneration;
  imageName: string;
  imageSize: number;
  imageSource?: "local" | "server";
  imageEtag?: string;
  startedAt: number;
  finishedAt?: number;
  targets: readonly AmlogicBatchTargetRun[];
}

export interface AmlogicBatchCounters {
  total: number;
  queued: number;
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  skipped: number;
}

const terminalStates = new Set<AmlogicBatchTargetState>(["completed", "failed", "cancelled", "skipped"]);

export function createBatchRun(input: {
  id: string;
  generation: AmlogicBatchGeneration;
  image: File;
  targets: readonly AmlogicBatchTargetSeed[];
  now?: number;
}): AmlogicBatchRunSnapshot {
  const startedAt = input.now ?? Date.now();
  return {
    id: input.id,
    generation: input.generation,
    imageName: input.image.name,
    imageSize: input.image.size,
    imageSource: isRemoteFirmwareFile(input.image) ? "server" : "local",
    imageEtag: isRemoteFirmwareFile(input.image) ? input.image.remoteMetadata.etag : undefined,
    startedAt,
    targets: input.targets.map((target) => ({
      sessionId: target.sessionId,
      label: target.label,
      state: target.eligible ? "queued" : "skipped",
      reason: target.reason,
      finishedAt: target.eligible ? undefined : startedAt,
    })),
  };
}

export function applyBatchEvent(snapshot: AmlogicBatchRunSnapshot, event: AmlogicBatchEvent, now = Date.now()): AmlogicBatchRunSnapshot {
  if (event.batchId !== snapshot.id) return snapshot;
  const sourceTargets = snapshot.targets.some((target) => target.sessionId === event.session.id)
    ? snapshot.targets
    : [...snapshot.targets, { sessionId: event.session.id, label: event.session.label, state: "queued" as const }];
  const targets = sourceTargets.map((target) => {
    if (target.sessionId !== event.session.id) return target;
    const state: AmlogicBatchTargetState = event.type === "progress" ? "running" : event.type;
    return {
      ...target,
      state,
      jobId: event.jobId,
      progress: event.progress ?? target.progress,
      startedAt: state === "running" && !target.startedAt ? now : target.startedAt,
      finishedAt: terminalStates.has(state) ? now : undefined,
      error: event.type === "failed" ? errorMessage(event.error) : target.error,
    };
  });
  const finishedAt = targets.every((target) => terminalStates.has(target.state)) ? now : undefined;
  return { ...snapshot, targets, finishedAt };
}

export function getBatchCounters(snapshot?: AmlogicBatchRunSnapshot): AmlogicBatchCounters {
  const counters: AmlogicBatchCounters = { total: 0, queued: 0, running: 0, completed: 0, failed: 0, cancelled: 0, skipped: 0 };
  if (!snapshot) return counters;
  counters.total = snapshot.targets.length;
  for (const target of snapshot.targets) counters[target.state] += 1;
  return counters;
}

export function isBatchActive(snapshot?: AmlogicBatchRunSnapshot): boolean {
  return Boolean(snapshot && !snapshot.finishedAt && snapshot.targets.some((target) => !terminalStates.has(target.state)));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? "Unknown error");
}
